// ----------------------------------------------------------------------
// Roles (PRD v0) — single source of truth, matching backend RoleType.
// ----------------------------------------------------------------------

export const USER_ROLES = ["INTERNAL", "SUPER_ADMIN", "MANAGER", "RECRUITER"] as const

export type UserRole = (typeof USER_ROLES)[number]

/** Hierarchy high → low. */
export const USER_ROLE_ORDER: UserRole[] = [...USER_ROLES]

export const ROLE_DISPLAY_LABELS: Record<UserRole, string> = {
    SUPER_ADMIN: "Super Admin",
    INTERNAL: "Internal",
    MANAGER: "Manager",
    RECRUITER: "Recruiter",
}

/** Named role values for comparisons — import instead of hardcoding strings. */
export const ROLES: { [Key in UserRole]: Key } = {
    SUPER_ADMIN: "SUPER_ADMIN",
    INTERNAL: "INTERNAL",
    MANAGER: "MANAGER",
    RECRUITER: "RECRUITER",
}

/** Roles that can see the Credits / billing nav item. */
export const BILLING_VISIBLE_ROLES: UserRole[] = [ROLES.SUPER_ADMIN, ROLES.INTERNAL, ROLES.MANAGER]

export function getRoleDisplayLabel(role: string | null | undefined): string {
    if (!role) return ""
    return ROLE_DISPLAY_LABELS[role as UserRole] ?? role
}

/** Roles assignable via internal APIs (all PRD v0 roles). */
export const WORKSPACE_ROLE_OPTIONS = USER_ROLE_ORDER.map((value) => ({
    value,
    label: ROLE_DISPLAY_LABELS[value],
}))

export type WorkspaceRoleValue = UserRole

export const DEFAULT_WORKSPACE_ROLE: WorkspaceRoleValue = "SUPER_ADMIN"

/** Roles assignable when creating/editing workspace users (excludes INTERNAL — sales/platform). */
export const WORKSPACE_USER_ROLE_OPTIONS = WORKSPACE_ROLE_OPTIONS.filter(
    (option) => option.value !== "INTERNAL"
)

export const DEFAULT_WORKSPACE_USER_ROLE: WorkspaceRoleValue = "MANAGER"

/** Fallback for role select when API returns a value not in WORKSPACE_ROLE_OPTIONS */
export const getEditableWorkspaceRole = (role: string): WorkspaceRoleValue =>
    USER_ROLES.includes(role as UserRole) ? (role as WorkspaceRoleValue) : DEFAULT_WORKSPACE_ROLE

// ----------------------------------------------------------------------
// Charging type (charge attempt rule) used in Credits & VAS and Create Workspace.
// ----------------------------------------------------------------------

export const CHARGING_TYPE_OPTIONS = [
    { value: "CHARGE_ALL_ATTEMPTS", label: "Charge All Attempts" },
    { value: "CHARGE_ONLY_FIRST_ATTEMPT", label: "Charge Only First Attempt" },
    { value: "CHARGE_CONCLUSIVE_ATTEMPTS", label: "Charge Conclusive Attempts" },
] as const

export type ChargingTypeValue = (typeof CHARGING_TYPE_OPTIONS)[number]["value"]

export const DEFAULT_CHARGING_TYPE: ChargingTypeValue = "CHARGE_ALL_ATTEMPTS"

// ----------------------------------------------------------------------
// Job status (ACTIVE, DRAFT, INACTIVE, PUBLISHED) used in JobsAndLeads and JobLeadsDetail.
// ----------------------------------------------------------------------

export type JobStatus = "ACTIVE" | "DRAFT" | "INACTIVE" | "PUBLISHED"

export const JOB_STATUS_PILL_SX: Record<JobStatus, { bgcolor: string; color: string }> = {
    DRAFT: { bgcolor: "grey.300", color: "grey.800" },
    ACTIVE: { bgcolor: "success.light", color: "common.white" },
    PUBLISHED: { bgcolor: "success.dark", color: "common.white" },
    INACTIVE: { bgcolor: "error.main", color: "common.white" },
}

export function normalizeJobStatus(value: string | undefined): JobStatus {
    if (!value) return "INACTIVE"
    const normalizedValue = value.toUpperCase()
    if (normalizedValue === "ACTIVE" || normalizedValue === "DRAFT" || normalizedValue === "INACTIVE" || normalizedValue === "PUBLISHED") {
        return normalizedValue as JobStatus
    }
    return "INACTIVE"
}

export function getJobStatusPillSx(status: string | undefined): { bgcolor: string; color: string } {
    return JOB_STATUS_PILL_SX[normalizeJobStatus(status)]
}
